import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useScorecardContext } from 'contexts';
import { selectData } from 'store/scorecards';
import { ScorecardGoal, ScorecardSubGoal } from 'store/interfaces';
import { SCORECARD_GOAL_TYPE_METRIC } from 'constants/scorecards';

interface UseHasEmptyAttainmentResult {
  hasEmptyAttainment: boolean;
  goalNames: string[];
}

// `metricAttainment` is typed as a number but the API sends it as a `toFixed(2)`
// string, so an empty value can arrive as null, undefined or ''.
const isAttainmentEmpty = (subgoal: ScorecardSubGoal) =>
  subgoal.metricAttainment === null ||
  subgoal.metricAttainment === undefined ||
  String(subgoal.metricAttainment).trim() === '';

export default function useHasEmptyAttainment(): UseHasEmptyAttainmentResult {
  const { id: scorecardId, store: scorecardStore } = useScorecardContext();
  const { data = null } =
    useSelector(selectData(scorecardStore, scorecardId)) || {};

  return useMemo(() => {
    const goals: ScorecardGoal[] = data?.goals ?? [];

    // A goal with weight 0 does not contribute to the Total Score, so a missing
    // attainment there cannot distort the completed scorecard. Compared against 0
    // rather than `> 0` so an absent weight still counts, matching useHasOnlyNAFeedback.
    const goalNames = goals
      .filter(
        (goal) =>
          goal.type === SCORECARD_GOAL_TYPE_METRIC &&
          goal.weight !== 0 &&
          (goal.subgoals ?? []).some(
            (subgoal) => subgoal.weight !== 0 && isAttainmentEmpty(subgoal),
          ),
      )
      .map((goal) => goal.name);

    return { hasEmptyAttainment: goalNames.length > 0, goalNames };
  }, [data?.goals]);
}
